import { ChangeDetectorRef, Component, EventEmitter, OnInit, Output, signal} from '@angular/core';
import {HttpStatusCode} from "@angular/common/http";
import {ActivatedRoute} from "@angular/router";
import {FormGroup} from "@angular/forms";
import {forkJoin, of} from "rxjs";
import {ResponseHttpError, ResponseHttpSuccess, CustomMessageService, UtilService, CustomMessageKey} from "library";
import {SalesWorkflowService} from "projects/store/src/app/services/api/v1/sales-workflow.service";
import {RequestService} from "projects/store/src/app/services/api/v1/request.service";
import {Category, SalesWorkflow} from "projects/store/src/app/enums";
import {MacroFlowService} from 'projects/store/src/app/services/api/v1/macro-flow.service';
import {AdditionalFieldTypes} from "projects/store/src/app/enums/additional-field-types";

@Component({
  selector: 'app-sales-additional-information-general',
  templateUrl: './sales-additional-information-general.component.html',
})
export class SalesAdditionalInformationGeneralComponent implements OnInit{
  form: FormGroup;
  requestId: string;
  protected flowCode: string;
  groupedFields!: any[];
  groupsTotal: number = 0;
  additionalDataValues: any;
  categoryCode: string;
  additionalFieldTypes = AdditionalFieldTypes;
  dropdownLevel1Filter: any;
  dropdownLevel2Filter: any;
  private _isFormInvalid = false;

  data = signal<any>({});
  formDisabled = true;

  showExperanzaClientSidebar = false

  @Output() nextStepEvent = new EventEmitter();

  constructor(private salesWorkflowService: SalesWorkflowService,
              private requestService: RequestService,
              private utilService: UtilService,
              private route: ActivatedRoute,
              private cdr: ChangeDetectorRef,
              private customMessageService: CustomMessageService,
              private macroFlowService: MacroFlowService) {
    this.form = new FormGroup({});
    this.requestId = this.route.snapshot.params['request_id'];
    this.categoryCode = this.route.snapshot.params['category_code'];
    this.flowCode = this.route.snapshot.params['flow_code'];
  }

  get isExperanza(): boolean {
    return this.macroFlowService.isExperanzaFlow(this.flowCode);
  }

  ngOnInit() {
    this.load();
    this.data.set(this.requestService.request);
    this.form.statusChanges.subscribe(() => {
      setTimeout(() => {
        this.formDisabled = this.form.invalid || this.form.pending;
      });
    });
  }

  load() {
    this.utilService.enablePageLoading();
    const documentNumber = (this.requestService.request?.['person']?.['document_id_number'] || '').replace(/\./g, '');
    forkJoin({
      additionalDataFields: this.salesWorkflowService.getAdditionalDataFields(this.salesWorkflowService.currentStep.category_step_id),
      additionalDataValues: this.requestService.getAdditionalData(this.requestId),
      preLoadData: this.isExperanza && documentNumber
        ? this.requestService.getPreLoadData(documentNumber)
        : of(null)
    }).subscribe({
      next: (resp: any)=> {
        if(resp.additionalDataFields.status === HttpStatusCode.Ok) {
          this.groupedFields = resp.additionalDataFields.body.data.list;
          this.groupedFields.forEach((group: any) => {
            group.fields.forEach((field: any) => {
              if(Array.isArray(field.options)) {
                field.options.sort((a: any, b: any) => (a?.name ?? '').localeCompare(b?.name ?? '', 'es', {sensitivity: 'base'}));
              }
            });
          });
          this.groupsTotal = Object.keys(this.groupedFields).length;
        }else{
          this.customMessageService.responseNoticeHandler(resp);
        }

        if(resp.additionalDataValues.status === HttpStatusCode.Ok) {
          this.groupedFields = this.groupedFields.map((group: any) => {
            group.fields.map((field: any) => {
              let data = resp.additionalDataValues.body.data.list.filter((row: any) => row.id == field.id);
              if(data.length === 1) {
                if([this.additionalFieldTypes['dropdown-level1'], this.additionalFieldTypes['dropdown-level2'], this.additionalFieldTypes['dropdown-level3']].includes(data[0].type)) {
                  field.value = JSON.parse(data[0].value);
                }else{
                  field.value = data[0].value;
                }
                if(field.type == this.additionalFieldTypes['dropdown-level1']) {
                  this.dropdownLevel1Filter = field.value
                  this.dropdownLevel2Filter = {id: 0, name: ''}
                }
                if(field.type == this.additionalFieldTypes['dropdown-level2']) {
                  this.dropdownLevel2Filter = field.value
                }
              }
              return field;
            });
            return group;
          });

        }else{
          this.customMessageService.responseNoticeHandler(resp);
        }

        if(resp.preLoadData?.status === HttpStatusCode.Ok) {
          if(resp.preLoadData.body.data.list.length > 0) {
            this.showExperanzaClientSidebar = true;
          }
          this.patchFormWithPreLoadData(resp.preLoadData.body.data.list);
        }

        this.utilService.disablePageLoading();
        this.cdr.detectChanges();
      },
      error: (error: ResponseHttpError) => {
        this.customMessageService.responseNoticeHandler(error);
        this.utilService.disablePageLoading();
      }
    });
  }

  // Mapeo: key del campo en el formulario dinámico → propiedad en la respuesta del datastore
  private readonly preLoadFieldMapping: Record<string, string> = {
    'provincias': 'provincia',
    'regiones': 'departamentoRegion',
    'distritos': 'distrito',
    'residencia': 'direccion'
  };

  private readonly dropdownTypes = [
    AdditionalFieldTypes['dropdown-level1'],
    AdditionalFieldTypes['dropdown-level2'],
    AdditionalFieldTypes['dropdown-level3'],
  ];

  private patchFormWithPreLoadData(preLoadList: any[]) {
    if (!preLoadList?.length) return;
    const preLoadData = preLoadList[0];

    for (const [fieldKey, preLoadKey] of Object.entries(this.preLoadFieldMapping)) {
      const value = preLoadData[preLoadKey];
      if (value == null) continue;

      const field = this.findFieldByKey(fieldKey);
      if (!field) continue;

      const isDropdown = this.dropdownTypes.includes(field.type);
      if (isDropdown) {
        this.patchDropdownField(field, value);
      } else {
        this.patchTextField(field, value);
      }
    }
  }

  private patchDropdownField(field: any, valueName: string) {
    const option = field.options?.find((opt: any) => opt.name?.toLowerCase() === valueName?.toLowerCase());
    if (!option) return;

    field.value = option;
    this.updateDropdownCascadeFilters(field.type, option);
    this.save({id: field.id, value: JSON.stringify(option)});
  }

  private patchTextField(field: any, value: string) {
    field.value = value;
    this.save({id: field.id, value});
  }

  private updateDropdownCascadeFilters(type: string, option: any) {
    if (type === this.additionalFieldTypes['dropdown-level1']) {
      this.dropdownLevel1Filter = option;
      this.dropdownLevel2Filter = {id: 0, name: ''};
    }
    if (type === this.additionalFieldTypes['dropdown-level2']) {
      this.dropdownLevel2Filter = option;
    }
  }

  private findFieldByKey(key: string): any {
    for (const group of this.groupedFields || []) {
      const found = group.fields?.find((f: any) => f.key === key);
      if (found) return found;
    }
    return null;
  }

  saveDropDown($event: any, field: any) {
    if(field.type == this.additionalFieldTypes['dropdown-level1']) {
      this.dropdownLevel1Filter = $event.value
      this.dropdownLevel2Filter = {id: 0, name: ''}
    }
    if(field.type == this.additionalFieldTypes['dropdown-level2']) {
      this.dropdownLevel2Filter = $event.value
    }
    this.save({...$event, value: JSON.stringify($event.value)});
  }

  save(data: {id: string, value: any, next?: boolean}) {
    this.requestService.saveAdditionalData(this.requestId, data).subscribe({
      next: (resp: ResponseHttpSuccess)=> {

      },
      error: (error: ResponseHttpError) => {
        this.customMessageService.responseNoticeHandler(error);
      }
    });
  }

  get isFormInvalid(): boolean {
      this._isFormInvalid = Object.keys(this.form.controls).some(controlName =>
        this.form.get(controlName)?.errors !== null
      );
    return this._isFormInvalid;
  }


  onNextStep() {
    this.nextStepEvent.emit();
  }

  protected get isFlowKrdya(): boolean {
    return this.flowCode === SalesWorkflow['app-krdya-cliente'];
  }


  protected readonly CustomMessageKey = CustomMessageKey;
  protected readonly Category = Category;

}
